import { Check, ChevronDown, Search } from "lucide-react";
import { Popover } from "radix-ui";
import { useId, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

export type MultiSelectOption = { value: string; label: string; icon?: ReactNode };

type MultiSelectProps = {
  /** Accessible name and the text before the summary: "Сотрудники: все". */
  label: string;
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  /** Summary when nothing is chosen, e.g. "все". */
  allLabel: string;
  className?: string;
};

/** A button that opens a searchable checkbox list; nothing chosen means "all". */
export function MultiSelect({ label, options, value, onChange, allLabel, className }: MultiSelectProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const listId = useId();
  const chosen = new Set(value);
  const q = query.trim().toLowerCase();
  const visible = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  const summary =
    value.length === 0
      ? allLabel
      : value.length === 1
        ? (options.find((o) => o.value === value[0])?.label ?? "1")
        : t("multiSelect.chosen", { count: value.length });
  const toggle = (v: string) => onChange(chosen.has(v) ? value.filter((x) => x !== v) : [...value, v]);

  return (
    <Popover.Root onOpenChange={(open) => !open && setQuery("")}>
      <Popover.Trigger
        className={cn(
          "inline-flex h-9 max-w-64 items-center gap-1.5 rounded-lg border bg-surface pr-2 pl-3 font-medium shadow-card",
          value.length > 0 && "border-primary/50",
          className,
        )}
        aria-label={`${label}: ${summary}`}
      >
        <span className="text-muted">{label}:</span>
        <span className="truncate">{summary}</span>
        <ChevronDown className="size-4 shrink-0 text-muted" aria-hidden="true" />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="z-50 flex w-72 flex-col rounded-lg border bg-surface shadow-lg"
        >
          {options.length > 7 && (
            <div className="relative border-b p-2">
              <Search
                className="absolute top-1/2 left-4 size-4 -translate-y-1/2 text-muted"
                aria-hidden="true"
              />
              <input
                type="search"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("multiSelect.search")}
                aria-label={t("multiSelect.search")}
                aria-controls={listId}
                className="h-8 w-full rounded-md border bg-surface pr-2 pl-8"
              />
            </div>
          )}
          <div id={listId} role="group" aria-label={label} className="max-h-72 overflow-y-auto p-1">
            {visible.length === 0 ? (
              <p className="px-2 py-3 text-center text-muted">{t("common.noResults")}</p>
            ) : (
              visible.map((o) => (
                <label
                  key={o.value}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-surface-muted"
                >
                  <input
                    type="checkbox"
                    className="peer sr-only"
                    checked={chosen.has(o.value)}
                    onChange={() => toggle(o.value)}
                  />
                  <span
                    className={cn(
                      "flex size-4 shrink-0 items-center justify-center rounded border peer-focus-visible:ring-2 peer-focus-visible:ring-primary",
                      chosen.has(o.value) && "border-primary bg-primary-solid text-white",
                    )}
                    aria-hidden="true"
                  >
                    {chosen.has(o.value) && <Check className="size-3" />}
                  </span>
                  {o.icon}
                  <span className="min-w-0 flex-1 truncate">{o.label}</span>
                </label>
              ))
            )}
          </div>
          {value.length > 0 && (
            <div className="border-t p-1">
              <button
                type="button"
                onClick={() => onChange([])}
                className="w-full rounded-md px-2 py-1.5 text-left font-medium text-primary hover:bg-surface-muted"
              >
                {t("multiSelect.clear")}
              </button>
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
